<template>
  <div class="board" :class="{ 'board--placing': placing }">
    <!-- the 16 track spaces, a clockwise ring in a 6x4 grid -->
    <button
      v-for="space in SPACES"
      :key="space.index"
      class="board__space"
      :class="{
        'board__space--legal': placing && legalSpaces.includes(space.index),
        'board__space--finishline': space.index === 0,
      }"
      :style="space.style"
      :disabled="!placing || !legalSpaces.includes(space.index)"
      @click="$emit('space', space.index)"
    >
      <span class="board__flag">{{ space.index + 1 }}</span>

      <span v-if="desertAt(space.index)" class="board__desert" :class="`board__desert--${desertAt(space.index).side}`">
        <img :src="`art/${desertAt(space.index).side}.jpg`" :alt="desertAt(space.index).side" />
        <span class="board__desert-owner">{{ ownerName(desertAt(space.index).seat) }}</span>
      </span>

      <span class="board__stack">
        <CamelChip
          v-for="(camel, height) in track[space.index]"
          :key="camel"
          :camel="camel"
          :riding="height > 0"
          :style="{ zIndex: height + 1 }"
        />
      </span>
    </button>

    <!-- everything past the finish line -->
    <div v-if="finished.length" class="board__finish">
      <span class="board__finish-label">Finish!</span>
      <span class="board__stack board__stack--finish">
        <CamelChip v-for="(camel, height) in finished" :key="camel" :camel="camel" :riding="height > 0" />
      </span>
    </div>

    <!-- the oasis in the middle: pyramid, rolled dice, whatever the table
         puts there -->
    <div class="board__centre">
      <slot />
    </div>
  </div>
</template>

<script>
// The race track: the board's 16-space ring redrawn as CSS (docs/board.jpg
// is the original). Space 1 sits bottom-left with the finish line beside
// it, and play runs clockwise: along the bottom, up the right, back along
// the top - matching the printed board's circuit closely enough that
// anyone who knows the cardboard finds their bearings.
import CamelChip from '@/components/game/CamelChip.vue';
import { TRACK_LENGTH } from '@/engine/constants';

// Ring coordinates: grid (row, col) in a 6-wide, 4-tall frame, clockwise
// from bottom-left.
const RING = [
  [4, 1], [4, 2], [4, 3], [4, 4], [4, 5], [4, 6], // 1-6 along the bottom
  [3, 6], [2, 6], // 7-8 up the right
  [1, 6], [1, 5], [1, 4], [1, 3], [1, 2], [1, 1], // 9-14 along the top
  [2, 1], [3, 1], // 15-16 down the left, finish beside 1
];

export default {
  name: 'GameBoard',
  components: { CamelChip },
  props: {
    track: { type: Array, required: true },
    desert: { type: Object, default: () => ({}) },
    players: { type: Array, default: () => [] },
    placing: { type: Boolean, default: false },
    legalSpaces: { type: Array, default: () => [] },
  },
  emits: ['space'],
  computed: {
    SPACES() {
      return RING.map(([row, col], index) => ({
        index,
        style: { gridRow: row, gridColumn: col },
      }));
    },
    finished() {
      const out = [];
      for (let space = TRACK_LENGTH; space < this.track.length; space += 1) {
        out.push(...this.track[space]);
      }
      return out;
    },
  },
  methods: {
    desertAt(space) {
      const entry = Object.entries(this.desert).find(([, tile]) => tile && tile.space === space);
      return entry ? { seat: Number(entry[0]), ...entry[1] } : null;
    },
    ownerName(seat) {
      const name = this.players[seat]?.name || '';
      return name.slice(0, 6);
    },
  },
};
</script>
